import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Space, Typography, Alert, Divider, message } from 'antd';
import { lookupMainMeterBySubMeter, rebootRelatedMains } from '../api';

const { Text, Paragraph } = Typography;

const isMainMeter = (device) => Number(device?.device_detail_type) === 1;

const meterNoOf = (device) =>
  String(device?.current_meter_no || device?.factory_meter_no || '').trim();

/**
 * 重启弹窗：一个「重启电表」→ reboot-related-mains（自己 ∪ 最近主题主表，后端去重）。
 * 旧小程序本机重启仍走 publish-by-meter，与本弹窗无关。
 */
const MeterRebootModal = ({ open, device, onClose }) => {
  const [rebootLoading, setRebootLoading] = useState(false);
  const [rebootHint, setRebootHint] = useState('');
  const [rebootError, setRebootError] = useState('');
  const [targetLabels, setTargetLabels] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [mainMeterNos, setMainMeterNos] = useState([]);
  const cancelledRef = useRef(false);

  const selfIsMain = isMainMeter(device);

  useEffect(() => {
    if (!open) {
      cancelledRef.current = true;
      return;
    }
    cancelledRef.current = false;
    setRebootLoading(false);
    setRebootHint('');
    setRebootError('');
    setTargetLabels([]);
    setLookupLoading(false);
    setLookupError('');
    if (isMainMeter(device)) {
      const no = meterNoOf(device);
      setMainMeterNos(no ? [no] : []);
    } else {
      setMainMeterNos([]);
    }
  }, [open, device?.id]);

  const handleReboot = async () => {
    if (!device?.id) return;
    setRebootLoading(true);
    setRebootHint('重启中');
    setRebootError('');
    setTargetLabels([]);
    try {
      const res = await rebootRelatedMains({ device_id: device.id });
      if (cancelledRef.current) return;
      const data = res?.data || {};
      const targets = Array.isArray(data.targets) ? data.targets : [];
      const labels = targets.map((t) => {
        const no = String(t.current_meter_no || t.publish_key || '').trim() || '-';
        const role = String(t.role || '') === 'self' ? '自己' : '主表';
        return `${no}（${role}）`;
      });
      if (labels.length) setTargetLabels(labels);

      const mainNos = [];
      targets.forEach((t) => {
        if (String(t.role || '') !== 'main') return;
        const no = String(t.current_meter_no || t.publish_key || '').trim();
        if (no && !mainNos.includes(no)) mainNos.push(no);
      });
      if (mainNos.length) setMainMeterNos(mainNos);

      if (!res?.success) {
        setRebootHint('');
        setRebootError('重启失败');
        message.error('重启失败');
        return;
      }
      setRebootHint(labels.length ? `重启成功：${labels.join('、')}` : '重启成功');
      setRebootError('');
      message.success('重启成功');
    } catch (e) {
      setRebootHint('');
      setRebootError('重启失败');
      message.error('重启失败');
    } finally {
      if (!cancelledRef.current) setRebootLoading(false);
    }
  };

  const handleLookupMain = async () => {
    if (!device) return;
    const q = meterNoOf(device);
    if (!q) {
      message.warning('当前设备无表号，无法查询主表');
      return;
    }
    setLookupLoading(true);
    setLookupError('');
    try {
      const res = await lookupMainMeterBySubMeter(q);
      if (!res?.success) {
        setLookupError(res?.msg || '查询失败');
        return;
      }
      const list = Array.isArray(res.data?.main_meters) ? res.data.main_meters : [];
      const nos = [];
      list.forEach((m) => {
        const no = String(m?.current_meter_no || m?.factory_meter_no || '').trim();
        if (no && !nos.includes(no)) nos.push(no);
      });
      setMainMeterNos(nos);
      if (!res.data?.latest_rx_topic) {
        setLookupError('未找到该表最近心跳，无法反查主表');
      } else if (!nos.length) {
        setLookupError('已找到 RX 主题，但未匹配到主表');
      } else {
        message.success(`找到 ${nos.length} 个主表表号`);
      }
    } catch (e) {
      setLookupError(e?.response?.data?.msg || e?.message || '查询失败');
    } finally {
      setLookupLoading(false);
    }
  };

  const meterLabel = meterNoOf(device) || device?.device_mac || device?.id || '';

  return (
    <Modal
      title={`重启${meterLabel ? ` · ${meterLabel}` : ''}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={480}
      destroyOnClose
      maskClosable={false}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text strong>重启操作</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            重启当前电表，并尝试重启最近心跳主题上的同房主表（去重；主副表登记写错也可至少打到自己）。
          </Paragraph>
          <Button type="primary" danger loading={rebootLoading} onClick={handleReboot}>
            重启电表
          </Button>
          <div style={{ minHeight: 40, marginTop: 8 }}>
            {rebootError ? (
              <Alert type="error" showIcon message={rebootError} />
            ) : rebootHint ? (
              <Alert type={rebootLoading ? 'info' : 'success'} showIcon message={rebootHint} />
            ) : null}
          </div>
          {targetLabels.length > 0 ? (
            <Alert type="info" showIcon message={`实际重启目标：${targetLabels.join('、')}`} style={{ marginTop: 8 }} />
          ) : null}
        </div>

        <Divider style={{ margin: '4px 0' }} />

        <div>
          <Text strong>主表表号（查询）</Text>
          <Paragraph type="secondary" style={{ marginBottom: 8, marginTop: 4 }}>
            {selfIsMain
              ? '当前设备档案为 4G 主表，显示本表表号；也可用查询核对。'
              : '与系统设置「主表查询」同款反查，仅展示，不代替上方重启目标。'}
          </Paragraph>

          {selfIsMain ? (
            <Alert
              type="success"
              showIcon
              message={
                mainMeterNos.length
                  ? `主表表号：${mainMeterNos.join('、')}`
                  : '主表档案缺少表号'
              }
            />
          ) : null}

          <Space direction="vertical" size="small" style={{ width: '100%', marginTop: selfIsMain ? 8 : 0 }}>
            <Button loading={lookupLoading} disabled={rebootLoading} onClick={handleLookupMain}>
              查询主表
            </Button>
            {!selfIsMain && mainMeterNos.length > 0 ? (
              <Alert type="success" showIcon message={`主表表号：${mainMeterNos.join('、')}`} />
            ) : null}
            {lookupError ? <Alert type="error" showIcon message={lookupError} /> : null}
          </Space>
        </div>
      </Space>
    </Modal>
  );
};

export default MeterRebootModal;
